import React, {useEffect, useRef, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import GanttChartComp from '../../../resources/gantt';
import { BoardContainerProps } from '../../types/board.types';
import {getTimelineData, getTimelineConfig, afterGridRenderView} from '../../utils/timeline';
import {useDispatch} from 'react-redux';
import {setMemberHighlighted, changeAiDates, aiCardClick} from '../../actions/card.actions';
import BoardService from '../../services/board.service';
import { imgUrl } from '../../../utils/url';
import { ActivityIssueTypes } from '../../types/ai.types';

type OverItemsCompProps = {
    items?: any[],
    handlers?: (item: any) => void,
    tagId?: any
};

const OverItemsComp = (props: OverItemsCompProps) => {
    let { items, handlers, tagId } = props;
    const tasks: any = items;
    // remove duplicate items in the array by checking with object id.
    if (typeof items !== 'undefined' && items.length) {
      items = items.filter((obj, pos, arr) => arr.map(mapObj => mapObj.id).indexOf(obj.id) === pos);
    }
    const divContent = items.map(item => <div className={`gantt-overlapping-item truncateText${tagId != item.wsId ? ' other-ws ' : ' '}`} data-id={item.id} data-wsid={item.id} onClick={() => handlers(item)}>{item.name}</div>);
    return (
      <div className="gantt-overlapping-items-wrapper">
        {divContent}
      </div>
    );
  };

const BoardTimeline = (props: BoardContainerProps) => {
    const dispatch = useDispatch();
    const {boardTimeline, boardDetails} = props;
    const [showButton, setTimelineButton] = useState<boolean>(false);
    const [timeline, viewTimeline] = useState<boolean>(false);
    const [config, setConfig] = useState<any>({});
    const { filteredTasks,
        reducedTasks} = getTimelineData(boardTimeline, boardDetails);
    const ganttPopupRoot = useRef<Root>();
    const { t } = useTranslation();

    const isShowTimelineButton = filteredTasks.length && reducedTasks.length;
    useEffect(() => {
        setPosForBoardTimeline();
    }, [isShowTimelineButton])
    
    if (!isShowTimelineButton) return null;

    const showTimeline = () => {
        const boardService = new BoardService();
        setTimelineButton(!showButton);
        viewTimeline(!showButton);
        const config = getTimelineConfig(boardTimeline, boardDetails);
        setConfig(config);
        if (boardDetails.tagId && !showButton) {
            boardService.fireWsTimelineEvent({ wsId: boardDetails.tagId });
        }
    }

    const setTaskListName = (task: any, index: number, active: any) => {
        console.log('setTaskListName', task, active);
        const memberId = (active) ? task.id : '';
        dispatch(setMemberHighlighted(memberId));
    }

    const changeTaskDates = (task: any, startdate: any, enddate: any) => {
        dispatch(changeAiDates(task));
    }


    const hideGanttPopup = () => {
        const ele = document.getElementById('gantt-popup');
        if (ele) {
            ele.style.display = 'none';
            ganttPopupRoot.current?.unmount();
        }
    }

    const openAI = (item: any) => {
        const { wsId, type } = item.wsId;
        hideGanttPopup();
        if (wsId == boardDetails.tagId && (!type || type == ActivityIssueTypes.ActionItem)) {
            const column = boardDetails.columns.filter((c: any) => c.data.find((d: any) => d.id == item.id));
            const columnId = column ? column[0]['id'] : '';
            if (columnId) {
                dispatch(aiCardClick({columnId, id: item.id}));
            }
        } else {
            const urlVal = `${wbConfig.baseUrl}/activity/aI?id=${item.id}`;
            setTimeout(() => openFullPopUp(aiFullViewPopUp, urlVal), 500);
        }
    }

    const showMiniPopup = (task: any, bar: any, event: any, position: any) => {
        console.log('showMiniPopup', task);
        hideGanttPopup();
        if (typeof task.wsId !== 'undefined') {
          const tagId = task.wsId;
          if (task.is_overlapping) {
            const { x, y } = position;
            const caly = y - 30;
            let calx = event.pageX - 300;
            const leftElement = document.querySelector('#leftmenu_navigation .leftmenu-wrapper');
            if (leftElement) {
                const keepopen = leftElement.getAttribute('data-keepopen');
                if (keepopen === 'true' || typeof keepopen === 'undefined') {
                    calx -= 260;
                }
            }
            calx = calx < 0 ? 30 : calx;
            const ele = document.getElementById('gantt-popup');
            if (ele) {
                ele.style.display = 'block';
                ele.style.left = `${calx}px`;
                ele.style.top = `${y}px`;
            }
            ganttPopupRoot.current = createRoot(ele);
            ganttPopupRoot.current.render(
                <OverItemsComp 
                    items={task.overlapping_items} 
                    handlers={openAI} 
                    tagId={boardDetails.tagId} 
                />
            );
          } else {
            openAI(task);
          }
        }
    }

    const events: any = {
        on_task_list_name: setTaskListName,
        on_show_details: showMiniPopup,
        on_date_change: changeTaskDates,
        on_after_grid_render: afterGridRenderView,
    }

    const captialize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
    
    const random = Math.random();
    if (Object.keys(config).length) {
        let timescale = document.querySelector('#gantt-timescale');
        if (timescale) {
            const ganttView = timescale.getAttribute('data-value');
            if (ganttView !== 'undefined') {
                config.view_mode = captialize(ganttView);
            }
        }
        let ganttSorting = document.querySelector('#gantt-sorting');
        if (ganttSorting) {
            const sortingView = ganttSorting.getAttribute('data-value');
            if (sortingView !== 'undefined') {
                config.sorting.id = sortingView;
            }
        }
    }

    return (
        <div>
            <div className="board-details-wrapper">
                <div className="board-details-left left" />
                <div className="board-details-right right" style={{display: 'none'}}>
                    <div className="board-timeline-wrapper">
                        <div className={`board-timeline-btn clickable ${showButton ? ' active ' : ''}`} 
                                onClick={() => showTimeline()}>
                            <div className="board-timeline-icon left">
                                <img src={imgUrl('/images/icons/board/timeline.png')} alt={t('Timeline')} />
                            </div>
                            <div className="board-timeline-text">
                                {t('Timeline')}
                            </div>
                            <div className="clear" />
                        </div>
                    </div>
                </div>
            <div className="clear" /></div>
            <div id="board-timeline-section">
                { timeline ? 
                <div className="left">
                    <div id="ws-task-name-wrapper" className="left" />
                    <GanttChartComp 
                        key={`gantt_chart_${random}`}
                        config={config} 
                        tasks={filteredTasks} 
                        class="ws-timeline-wrapper left " 
                        events={events} 
                        loading={false} />
                </div>
                    : ''
                }
            </div>
        </div>
    )
}

export default BoardTimeline;
